import { useEffect, useRef } from 'react';

import { bind } from 'bind-event-listener';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { findOverflowScrollParent } from '@atlaskit/editor-common/ui';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AIPlugin } from '../../../aiPluginType';

const SCROLL_MARGIN = 32;

/**
 * Calculates the cumulative offset from the editor DOM to its scroll container.
 * Walks up the DOM tree accumulating offsetTop values.
 *
 * @param editorDom - The editor's DOM element
 * @param scrollContainer - The scroll container element
 * @returns The total offset in pixels
 */
function calculateOffsetTopFromScrollContainer(
	editorDom: Element,
	scrollContainer: HTMLElement,
): number {
	let offset = 0;
	let el: Element | null = editorDom;

	while (el && el !== scrollContainer) {
		if (el instanceof HTMLElement) {
			offset += el.offsetTop;
			el = el.offsetParent;
		} else {
			break;
		}
	}

	return offset;
}

/**
 * Calculates the scroll position needed to show the start position at the top of the viewport.
 *
 * @returns The target scrollTop value
 */
function calculateTopScroll({
	view,
	streamStartPos,
	scrollParent,
}: {
	scrollParent: HTMLElement;
	streamStartPos: number;
	view: EditorView;
}): number {
	const startCoords = view.coordsAtPos(streamStartPos);
	const offsetTop = view.coordsAtPos(0).top;
	const offsetTopFromScrollContainer = calculateOffsetTopFromScrollContainer(
		view.dom,
		scrollParent,
	);

	return offsetTopFromScrollContainer + startCoords.top - offsetTop - SCROLL_MARGIN;
}

/**
 * Calculates the scroll position needed to show the target position at the bottom of the viewport.
 * Accounts for modal height to prevent content from being hidden.
 *
 * @returns The target scrollTop value
 */
function calculateBottomScroll({
	view,
	scrollTargetPos,
	scrollParent,
	modalHeight,
}: {
	modalHeight: number;
	scrollParent: HTMLElement;
	scrollTargetPos: number;
	view: EditorView;
}): number {
	const offsetTop = view.coordsAtPos(0).top;
	const offsetTopFromScrollContainer = calculateOffsetTopFromScrollContainer(
		view.dom,
		scrollParent,
	);

	// Handle edge case where coords could be 0 at the last position of doc
	let targetBottom = view.coordsAtPos(scrollTargetPos).bottom;
	if (targetBottom === 0 && scrollTargetPos > 0) {
		targetBottom = view.coordsAtPos(scrollTargetPos - 1).bottom;
	}

	return (
		offsetTopFromScrollContainer +
		targetBottom -
		offsetTop +
		modalHeight +
		SCROLL_MARGIN -
		scrollParent.offsetHeight
	);
}

/**
 * Manages auto-scroll behavior during AI content streaming.
 *
 * This hook automatically scrolls the viewport to keep streamed content visible
 * while respecting user scroll intentions. When the user manually scrolls during
 * streaming, auto-scroll is disabled for the remainder of that streaming session.
 *
 * ## Behavior:
 * - **Initial scroll**: When streaming starts, scrolls to show the start position
 * - **Continuous scroll**: As content streams in, keeps new content visible at the bottom
 * - **User override**: Detects manual user scrolls and disables auto-scroll
 * - **Modal awareness**: Accounts for AI modal height to prevent content obstruction
 *
 * ## Scroll Detection:
 * Uses a flag-based system to distinguish programmatic scrolls from user scrolls:
 * - Before programmatic scroll: Sets `isProgrammaticScrollRef` to `true`
 * - On any scroll event: Checks if flag is `true` (programmatic) or `false` (user)
 * - After any scroll event: Resets flag to `false`
 *
 * @param options - Hook options
 * @param options.api - The editor plugin API for accessing streaming state
 * @param options.view - The ProseMirror EditorView instance
 * @param options.modalRef - Optional ref to the AI modal for height calculations
 */
export const useStreamingScroll = ({
	api,
	view,
	modalRef,
}: {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	modalRef?: React.RefObject<HTMLDivElement>;
	view: EditorView | undefined;
}) => {
	const scrollParentRef = useRef<HTMLElement | null>(null);
	const hasUserScrolledRef = useRef(false);
	const isProgrammaticScrollRef = useRef(false);
	const wasStreamingRef = useRef(false);

	const { streamStartPos, scrollTargetPos, isStreaming } = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		(state) => ({
			streamStartPos: state.aiExperienceState?.streamingState?.streamStartPos,
			scrollTargetPos: state.aiExperienceState?.streamingState?.scrollTargetPos,
			isStreaming: state.aiExperienceState?.isStreaming ?? false,
		}),
	);

	useEffect(() => {
		if (!view) {
			return;
		}

		// Find and cache the scroll container
		const scrollParent = findOverflowScrollParent(view.dom);
		if (scrollParent) {
			scrollParentRef.current = scrollParent;
		}

		// Only attach scroll listener during active streaming
		if (!scrollParent || !isStreaming) {
			return;
		}

		/**
		 * Detects user scrolls by checking if the scroll was programmatic.
		 * - If isProgrammaticScrollRef is true: This scroll was triggered by our code → ignore it
		 * - If isProgrammaticScrollRef is false: This is a user scroll → disable auto-scroll
		 * - Always reset the flag after processing so the next scroll is treated as a user scroll
		 */
		const handleScroll = () => {
			if (!isProgrammaticScrollRef.current) {
				hasUserScrolledRef.current = true;
			}
			isProgrammaticScrollRef.current = false;
		};

		const unbind = bind(scrollParent, {
			type: 'scroll',
			listener: handleScroll,
			options: { passive: true },
		});

		return unbind;
	}, [view, isStreaming]);

	useEffect(() => {
		const scrollParent = scrollParentRef.current;

		// Reset user scroll flag only when streaming session starts (not on every update)
		const justStartedStreaming = isStreaming && !wasStreamingRef.current;
		if (justStartedStreaming) {
			hasUserScrolledRef.current = false;
		}
		wasStreamingRef.current = isStreaming;

		// Bail out early if conditions aren't met for auto-scroll
		if (hasUserScrolledRef.current || !scrollParent || !view || streamStartPos === undefined) {
			return;
		}

		// Attempt to perform auto-scroll
		// Wrapped in try-catch because positions may be temporarily invalid during document changes
		try {
			const modalHeight = modalRef?.current?.offsetHeight || 0;

			// Mark this as a programmatic scroll so the scroll listener doesn't treat it as a user scroll
			isProgrammaticScrollRef.current = true;

			// Case 1: Only start position is available → ensure start is visible at top
			if (scrollTargetPos === undefined) {
				const topScrollTop = calculateTopScroll({ view, streamStartPos, scrollParent });

				// Only scroll up if needed (don't scroll down, user might be reading above)
				if (scrollParent.scrollTop > topScrollTop) {
					scrollParent.scroll({ top: topScrollTop, behavior: 'instant' });
				}
				return;
			}

			// Case 2: Both start and target positions available → keep target visible at bottom
			const bottomScrollTop = calculateBottomScroll({
				view,
				scrollTargetPos,
				scrollParent,
				modalHeight,
			});

			// Only scroll down if needed (target is below viewport)
			if (scrollParent.scrollTop < bottomScrollTop) {
				scrollParent.scroll({ top: bottomScrollTop, behavior: 'instant' });
			}
		} catch {
			// Silently ignore errors - positions may be invalid during rapid document mutations
		}
	}, [streamStartPos, scrollTargetPos, isStreaming, view, modalRef]);
};
